export const SETUP_CHECKLIST = [
  {
    id: 'brand-identity',
    title: '1. Brand Identity',
    items: [
      { id: 'bi-1', title: 'Shop Name', description: "Finalise your shop name. Check it's available on Etsy, Shopify, and social platforms." },
      { id: 'bi-2', title: 'Logo Design', description: 'Create a primary logo. Tip: design for legibility at small sizes.', specs: 'PNG/SVG, Min 500x500px' },
      { id: 'bi-3', title: 'Logo Variations', description: 'Create horizontal, stacked, and icon-only versions.' },
      { id: 'bi-4', title: 'Color Palette', description: 'Define 2–3 primary brand colors + 1–2 accent colors.' },
      { id: 'bi-5', title: 'Typography', description: 'Select a heading font and a body font that represent your brand personality.' },
      { id: 'bi-6', title: 'Brand Voice', description: "Write 2–3 sentences describing your shop's tone (e.g., playful, minimal)." },
    ]
  },
  {
    id: 'etsy-setup',
    title: '2. Etsy Shop Setup',
    items: [
      { id: 'et-1', title: 'Open Etsy Shop', description: 'Create account → Open a shop → Choose language, country, and currency.' },
      { id: 'et-2', title: 'Shop Icon & Banner', description: 'Upload logo (500x500px) and a shop banner (2400x600px).' },
      { id: 'et-3', title: 'About Section', description: 'Tell your story. Include photos of your process.' },
      { id: 'et-4', title: 'Shop Members', description: 'Add yourself and any partners. Be transparent about POD partners.' },
      { id: 'et-5', title: 'Shop Title', description: 'Write a brief, keyword-rich description (max 55 characters).' },
      { id: 'et-6', title: 'Shop Announcement', description: 'Welcome buyers and mention current sales or updates.' },
      { id: 'et-7', title: 'Shop Policies', description: 'Complete: Processing times, Returns & exchanges, FAQs.' },
      { id: 'et-8', title: 'Payment & Billing', description: 'Connect Etsy Payments and verified bank account.' },
    ]
  },
  {
    id: 'shopify-setup',
    title: '2. Shopify Store Setup',
    items: [
      { id: 'sh-1', title: 'Create Shopify Account', description: 'Choose a plan and set your store name.' },
      { id: 'sh-2', title: 'Custom Domain', description: 'Purchase or connect a custom .com domain.' },
      { id: 'sh-3', title: 'Theme Selection', description: 'Customize a theme. Focus on mobile responsiveness.' },
      { id: 'sh-4', title: 'Legal Pages', description: 'Generate: Refund, Privacy, Terms, and Shipping policies.' },
      { id: 'sh-5', title: 'Payment Gateway', description: 'Set up Shopify Payments and PayPal.' },
      { id: 'sh-6', title: 'Shipping Profiles', description: 'Set up flat rates based on your POD provider.' },
      { id: 'sh-7', title: 'Tax Settings', description: 'Configure tax collection based on your location.' },
      { id: 'sh-8', title: 'Navigation Menus', description: 'Create Main Menu and Footer Menu.' },
      { id: 'sh-9', title: 'Notification Emails', description: 'Customize Order Confirmation with your logo.' },
      { id: 'sh-10', title: 'POD Integration', description: 'Install and connect Printful or Printify app.' },
      { id: 'sh-11', title: 'Analytics Setup', description: 'Connect Google Analytics 4 and Meta Pixel.' },
      { id: 'sh-12', title: 'Test Order', description: 'Place a real test order to verify payments.' },
    ]
  },
  {
    id: 'pod-integration',
    title: '3. POD Provider Integration',
    items: [
      { id: 'pod-1', title: 'Account Creation', description: 'Sign up for Printful, Printify, or Gelato.' },
      { id: 'pod-2', title: 'Billing Method', description: 'Connect a card to pay for orders before customer funds clear.' },
      { id: 'pod-3', title: 'Shipping Profiles Sync', description: 'Ensure the POD shipping rates match your store settings.' },
      { id: 'pod-4', title: 'Product Templates', description: 'Create 5 core product templates (e.g., Bella+Canvas 3001).' },
      { id: 'pod-5', title: 'Automatic Fulfillment', description: 'Enable automatic order processing for hands-off operations.' },
      { id: 'pod-6', title: 'Sample Orders', description: 'Order 1-2 samples to verify print quality and fit.' },
    ]
  },
  {
    id: 'seo-strategy',
    title: '4. Research & SEO Strategy',
    items: [
      { id: 'seo-1', title: 'Niche Validation', description: 'Confirm search volume is high enough for your selected niche.' },
      { id: 'seo-2', title: 'Master Keyword List', description: 'Draft 20 primary and 50 long-tail keywords.' },
      { id: 'seo-3', title: 'Competitor Benchmarking', description: 'Analyze top 5 stores in your niche for pricing and style.' },
      { id: 'seo-4', title: 'Product Title Formula', description: 'Create a repeatable structure for high-converting titles.' },
      { id: 'seo-5', title: 'Tagging Strategy', description: 'Define how you will use all 13 tags on Etsy for maximum reach.' },
    ]
  },
  {
    id: 'social-media',
    title: '5. Social Media Presence',
    items: [
      // Facebook
      { id: 'fb-1', title: 'Facebook: Create Page', description: 'Create a Facebook Business Page (not a personal profile).' },
      { id: 'fb-2', title: 'Facebook: Profile Picture', description: 'Upload profile picture.', specs: '170×170px' },
      { id: 'fb-3', title: 'Facebook: Cover Photo', description: 'Upload cover photo. Tip: keep key content in center 560px.', specs: '851×315px' },
      { id: 'fb-4', title: 'Facebook: Page Bio', description: 'Write Page bio. Include shop URL.', specs: 'Max 255 chars' },
      { id: 'fb-5', title: 'Facebook: Call-to-action', description: 'Add CTA button (e.g., "Shop Now") linking to your store.' },
      // Instagram
      { id: 'ig-1', title: 'Instagram: Create Account', description: 'Convert to or create an Instagram Business Account.' },
      { id: 'ig-2', title: 'Instagram: Profile Picture', description: 'Use logo icon version.', specs: '320×320px' },
      { id: 'ig-3', title: 'Instagram: Bio', description: 'Write bio. Include one link (use Linktree if needed).', specs: 'Max 150 chars' },
      { id: 'ig-4', title: 'Instagram: Grid Planning', description: 'Plan a 9-post grid theme for launch.' },
      { id: 'ig-5', title: 'Instagram: Shopping', description: 'Set up Instagram Shopping (link to Shopify/Etsy catalog if eligible).' },
      // Pinterest
      { id: 'pn-1', title: 'Pinterest: Create Account', description: 'Create or convert to a Pinterest Business Account.' },
      { id: 'pn-2', title: 'Pinterest: Profile Picture', description: 'Upload profile picture.', specs: '165×165px' },
      { id: 'pn-3', title: 'Pinterest: Board Setup', description: 'Create boards relevant to your niche (min 3 boards at launch).', specs: 'Covers: 800×450px' },
      { id: 'pn-4', title: 'Pinterest: Pin Templates', description: 'Create product pin templates. Tip: this is the highest-performing ratio.', specs: '1000×1500px (2:3)' },
      { id: 'pn-5', title: 'Pinterest: Rich Pins', description: 'Enable Rich Pins and verify your website with Pinterest.' },
      // Twitter (X)
      { id: 'tw-1', title: 'X (Twitter): Create Account', description: 'Create account with your brand handle.' },
      { id: 'tw-2', title: 'X (Twitter): Profile Picture', description: 'Upload profile picture.', specs: '400×400px' },
      { id: 'tw-3', title: 'X (Twitter): Header Image', description: 'Upload banner. Safe zone: center 1260×360px.', specs: '1500×500px' },
      { id: 'tw-4', title: 'X (Twitter): Bio', description: 'Write bio. Include shop link.', specs: 'Max 160 chars' },
      { id: 'tw-5', title: 'X (Twitter): Pinned Tweet', description: 'Pin a launch announcement tweet.' },
    ]
  },
  {
    id: 'operations',
    title: '6. Operations & Launch',
    items: [
      { id: 'ops-1', title: 'Customer Service Templates', description: 'Draft "Where is my order?" and "Return" email responses.' },
      { id: 'ops-2', title: 'Packaging Inserts', description: 'Design a digital or physical thank you note/discount card.' },
      { id: 'ops-3', title: 'Return Policy Verification', description: 'Confirm your POD provider’s return window matches yours.' },
      { id: 'ops-4', title: 'Final Store Audit', description: 'Check every link, image, and price point on your live store.' },
      { id: 'ops-5', title: 'Launch Announcement', description: 'Post your official launch on all social channels.' },
    ]
  }
];

export const INITIAL_PROTOCOLS = [
  {
    id: 'proto-research',
    title: 'Master Research Protocol',
    description: 'Strategic steps for high-conversion market analysis.',
    iconName: 'Search',
    colorClass: 'bg-terracotta',
    tasks: [
      { id: 'res-1', title: 'Trend Analysis', description: 'Search keywords. Note top styles, colors, and bestseller badges.', specs: 'Etsy' },
      { id: 'res-2', title: 'Niche Research', description: 'Identify target niche using Pinterest and Google Trends.', specs: 'Strategy' },
      { id: 'res-3', title: 'Competitor Analysis', description: 'Benchmark 5 top competitors on pricing and design.', specs: 'Competitors' },
      { id: 'res-4', title: 'Keyword Research', description: 'Target 1–2 primary + 5–8 long-tail keywords per product.', specs: 'SEO' },
      { id: 'res-5', title: 'Product Selection', description: "Check POD supplier for profit margins and quality.", specs: 'POD' },
      { id: 'res-6', title: 'Pricing Research', description: 'Calculate cost and target 30–50% margin.', specs: 'Finance' },
      { id: 'res-7', title: 'Validate Demand', description: 'Confirm search volume > 0 on primary marketplaces.', specs: 'Validation' }
    ]
  },
  {
    id: 'proto-production',
    title: 'Design Production',
    description: 'Technical execution and quality assurance standards.',
    iconName: 'Layers',
    colorClass: 'bg-sage',
    tasks: [
      { id: 'prod-1', title: 'Initial Sketch/Layout', description: 'Draft basic composition and typography placement.', specs: 'Draft' },
      { id: 'prod-2', title: 'Final Artwork', description: 'Export high-resolution file at 300 DPI.', specs: '300 DPI' },
      { id: 'prod-3', title: 'Color Proofing', description: 'Check appearance on both light and dark backgrounds.', specs: 'QA' },
      { id: 'prod-4', title: 'Mockup Creation', description: 'Apply design to 3+ high-quality lifestyle mockups.', specs: 'Marketing' },
      { id: 'prod-5', title: 'File Organization', description: 'Save source files in the designated project folder.', specs: 'Admin' },
      { id: 'prod-6', title: 'Final Review', description: 'Check for spelling errors and alignment issues.', specs: 'QC' }
    ]
  }
];

export const WORKFLOW_STAGES = ['Ideas', 'In Design', 'Mockup Review', 'Listed', 'Live'];

export const PRODUCT_TYPES = [
  'T-Shirt', 'Sweatshirt', 'Hoodie', 'Mug', 'Tote Bag', 'Poster', 'Phone Case', 'Sticker', 'Hat', 'Notebook', 'Other'
];

export const TIMELINE_COLORS = [
  { name: 'Terracotta', value: '#C4622D' },
  { name: 'Sage', value: '#7A9E7E' },
  { name: 'Espresso', value: '#1C1209' },
  { name: 'Ochre', value: '#D4A373' },
  { name: 'Slate', value: '#4A5568' },
  { name: 'Wine', value: '#800020' }
];